import type { SignInMethod } from './events';

/**
 * Every sign-in ends in a full page load — the app reloads once the session is
 * stored, or comes back from Cognito or Microsoft — so the page that signs the
 * user in never gets to see them signed in. The sign-in code leaves this note
 * in sessionStorage (per tab, kept across same-tab redirects to other sites)
 * and the next load turns it into a `User Signed In` event, which is how a
 * real sign-in is told apart from opening the app on a stored session.
 */
const SIGN_IN_KEY = 'analytics.signIn';

const METHODS: readonly SignInMethod[] = [
  'password',
  'google',
  'microsoft',
  'hosted_ui',
];

export const markSignIn = (method: SignInMethod): void => {
  try {
    sessionStorage.setItem(SIGN_IN_KEY, method);
  } catch {
    // Storage blocked: the sign-in shows up as a session start instead.
  }
};

/** Reads and clears the note, so one sign-in is only ever counted once. */
export const consumeSignIn = (): SignInMethod | null => {
  try {
    const method = sessionStorage.getItem(SIGN_IN_KEY);
    sessionStorage.removeItem(SIGN_IN_KEY);
    return METHODS.find((known) => known === method) ?? null;
  } catch {
    return null;
  }
};
